<template>
  <ion-page>
    <PageHeaderWithMenu title="Разделы медиатеки" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Добавить подраздел медиатеки</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-select label="Главный раздел" label-placement="stacked" v-model="selectedMediaCategory"
              interface="popover" mode="ios" placeholder="Выберите раздел" @ionChange="onMediaCategoryChange()">
              <ion-select-option v-for="category in mediaCategories" :key="category.key" :value="category.key">{{ category.label }}</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-item>
            <ion-input label="Название (для пользователей)" label-placement="stacked" v-model="newSection.name"
              placeholder="Например: Академия"></ion-input>
          </ion-item>
          <label class="section-toggle section-toggle--form">
            <span>Доступно всем</span>
            <ion-toggle v-model="newSection.availableToAll" mode="ios"></ion-toggle>
          </label>
          <ion-item :class="['section-role-select', { 'section-role-select--disabled': newSection.availableToAll }]">
            <ion-select label="Роль доступа" label-placement="stacked" v-model="newSection.roleName"
              interface="popover" :interface-options="roleSelectPopoverOptions" mode="ios" placeholder="Выберите роль" :disabled="newSection.availableToAll">
              <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-item>
            <ion-input label="Ключ папки (латиница)" label-placement="stacked" v-model="newSection.folderKey"
              placeholder="Academia, CoachGP, Ted"></ion-input>
          </ion-item>
          <ion-button expand="block" @click="addSection()" :disabled="!canAddSection">Добавить подраздел</ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Список подразделов: {{ mediaCategoryTitle }}</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <div v-if="sections.length" class="sections-list">
            <div v-for="s in sections" :key="s.id || s.folderKey" class="section-row">
              <div class="section-info">
                <div class="section-title-row">
                  <h2>{{ s.name }}</h2>
                  <span :class="['section-status', s.availableToAll ? 'section-status--public' : 'section-status--role']">
                    {{ s.availableToAll ? 'Доступно всем' : 'По роли' }}
                  </span>
                </div>
                <p class="section-key">Папка: {{ s.folderKey }}</p>
                <p class="section-role">{{ s.availableToAll ? 'Раздел виден всем пользователям' : roleLabel(s.roleName) }}</p>
              </div>

              <div class="section-controls">
                <label class="section-toggle">
                  <span>Доступно всем</span>
                  <ion-toggle v-model="s.availableToAll" mode="ios" @ionChange="saveSection(s)"></ion-toggle>
                </label>

                <ion-item lines="none" :class="['section-role-select', { 'section-role-select--disabled': s.availableToAll }]">
                  <ion-select
                    label="Роль доступа"
                    label-placement="stacked"
                    v-model="s.roleName"
                    interface="popover"
                    :interface-options="roleSelectPopoverOptions"
                    mode="ios"
                    placeholder="Выберите роль"
                    :disabled="s.availableToAll"
                    @ionChange="saveSection(s)"
                  >
                    <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
                  </ion-select>
                </ion-item>

                <div class="section-actions">
                  <ion-button fill="clear" color="medium" size="small" @click="renameSection(s)" v-if="s.id" title="Переименовать">
                    <ion-icon slot="icon-only" :icon="pencilOutline"></ion-icon>
                  </ion-button>
                  <ion-button fill="clear" color="danger" size="small" @click="deleteSection(s)" v-if="s.id" title="Удалить">
                    <ion-icon slot="icon-only" :icon="trashOutline"></ion-icon>
                  </ion-button>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="empty-hint">Разделов пока нет.</p>
        </ion-card-content>
      </ion-card>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { Authority } from '@/model/authority.model';
import { EducationSection } from '@/model/education-section.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { DEFAULT_MEDIA_CATEGORY, MEDIA_CATEGORIES, MediaCategoryKey, mediaCategoryLabel } from '@/model/media-category.model';
import AdminService from '@/services/admin.service';
import { CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonToggle,
  alertController,
  toastController,
} from '@ionic/vue';
import { pencilOutline, trashOutline } from 'ionicons/icons';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import axios from 'axios';
import { defineComponent, inject } from 'vue';

export default defineComponent({
  name: 'AdminEducationSections',
  components: {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonIcon,
    IonInput,
    IonItem,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonToggle,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return { isMobile: globalContext.isMobile, pencilOutline, trashOutline };
  },
  data() {
    return {
      adminService: new AdminService(),
      allAuthorities: [] as Authority[],
      sections: [] as EducationSection[],
      mediaCategories: MEDIA_CATEGORIES,
      selectedMediaCategory: DEFAULT_MEDIA_CATEGORY as MediaCategoryKey,
      newSection: {
        name: '',
        roleName: '',
        availableToAll: false,
        folderKey: '',
      },
      savingId: null as number | null,
      roleSelectPopoverOptions: {
        cssClass: 'admin-role-select-popover',
        side: 'top',
        alignment: 'center',
      },
      headerScrolled: false,
    };
  },
  computed: {
    mediaCategoryTitle(): string {
      return mediaCategoryLabel(this.selectedMediaCategory);
    },
    canAddSection(): boolean {
      return !!(
        this.newSection.name?.trim() &&
        this.newSection.folderKey?.trim() &&
        (this.newSection.availableToAll || this.newSection.roleName)
      );
    },
  },
  mounted() {
    this.loadAuthorities();
    this.loadSections();
  },
  ionViewDidEnter() {
    this.loadAuthorities();
    this.loadSections();
  },
  watch: {
    '$store.getters.rolesUpdatedAt'() {
      this.loadAuthorities();
    },
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    onMediaCategoryChange() {
      this.loadSections();
    },
    async loadAuthorities() {
      try {
        const token = (await Preferences.get({ key: 'jwt' })).value;
        const url = `${process.env.VUE_APP_API_URL}api/authorities-full`;
        if (this.isMobile && token) {
          const res = await CapacitorHttp.get({ url, headers: { Authorization: `Bearer ${token}` }, readTimeout: 60000 });
          this.allAuthorities = (res.data || []).sort((a: Authority, b: Authority) =>
            (a.description || '').localeCompare(b.description || ''));
        } else {
          const { data } = await axios.get<Authority[]>('api/authorities-full');
          this.allAuthorities = (data || []).sort((a, b) => (a.description || '').localeCompare(b.description || ''));
        }
      } catch (e) {
        console.error('loadAuthorities', e);
      }
    },
    async loadSections() {
      try {
        this.sections = await this.adminService.getEducationSections(this.selectedMediaCategory);
      } catch (e) {
        console.error('loadSections', e);
      }
    },
    async addSection() {
      if (!this.canAddSection) return;
      try {
        await this.adminService.createEducationSection({
          name: this.newSection.name.trim(),
          roleName: this.newSection.availableToAll ? null : this.newSection.roleName,
          availableToAll: this.newSection.availableToAll,
          folderKey: this.newSection.folderKey.trim(),
          mediaCategory: this.selectedMediaCategory,
        });
        const toast = await toastController.create({
          message: 'Раздел добавлен',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.newSection = { name: '', roleName: '', availableToAll: false, folderKey: '' };
        this.loadSections();
      } catch (e) {
        console.error('addSection', e);
        const toast = await toastController.create({
          message: 'Ошибка добавления раздела',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    async saveSection(s: EducationSection) {
      if (!s.id) return;
      if (!s.availableToAll && !s.roleName) {
        await this.showToast('Для закрытого раздела нужно выбрать роль', 'warning');
        return;
      }
      this.savingId = s.id;
      try {
        const saved = await this.adminService.updateEducationSection(s.id, {
          name: s.name,
          roleName: s.availableToAll ? null : s.roleName,
          availableToAll: s.availableToAll,
          folderKey: s.folderKey,
          mediaCategory: s.mediaCategory || this.selectedMediaCategory,
          sortOrder: s.sortOrder,
        });
        const idx = this.sections.findIndex(section => section.id === saved.id);
        if (idx >= 0) {
          this.sections.splice(idx, 1, saved);
        }
        await this.showToast('Раздел сохранён', 'success');
      } catch (e) {
        console.error('saveSection', e);
        await this.showToast('Не удалось сохранить раздел', 'danger');
      } finally {
        this.savingId = null;
      }
    },
    async renameSection(s: EducationSection) {
      const sectionId = s.id;
      if (!sectionId) return;
      const alert = await alertController.create({
        header: 'Переименовать раздел',
        cssClass: 'alert-dark-theme',
        inputs: [
          {
            name: 'name',
            type: 'text',
            value: s.name,
            placeholder: 'Название для пользователей',
          },
        ],
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Сохранить',
            handler: async (data: { name?: string }) => {
              const newName = (data.name || '').trim();
              if (!newName) {
                const toast = await toastController.create({
                  message: 'Название не может быть пустым.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
                return false;
              }
              if (newName === s.name) return true;
              try {
                await this.adminService.updateEducationSection(sectionId, {
                  name: newName,
                  roleName: s.availableToAll ? null : s.roleName,
                  availableToAll: s.availableToAll,
                  folderKey: s.folderKey,
                  mediaCategory: s.mediaCategory || this.selectedMediaCategory,
                  sortOrder: s.sortOrder,
                });
                const toast = await toastController.create({
                  message: 'Название раздела обновлено.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadSections();
              } catch (e) {
                console.error('renameSection', e);
                const toast = await toastController.create({
                  message: 'Ошибка при переименовании.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
              return true;
            },
          },
        ],
      });
      await alert.present();
    },
    async deleteSection(s: EducationSection) {
      if (!s.id) return;
      try {
        await this.adminService.deleteEducationSection(s.id);
        const toast = await toastController.create({
          message: 'Раздел удалён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.loadSections();
      } catch (e) {
        console.error('deleteSection', e);
        const toast = await toastController.create({
          message: 'Ошибка удаления раздела',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    roleLabel(roleName?: string | null): string {
      if (!roleName) {
        return 'Роль не выбрана';
      }
      const authority = this.allAuthorities.find(a => a.name === roleName);
      return authority?.description || roleName;
    },
    async showToast(message: string, color: string) {
      const toast = await toastController.create({
        message,
        duration: 2000,
        position: 'top',
        color,
      });
      await toast.present();
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--ion-color-dark);
}

.admin-card ion-card-title {
  color: white;
}

.admin-card ion-card-content {
  color: rgba(255, 255, 255, 0.85);
}

.admin-card ion-item {
  --background: transparent;
  --color: white;
}

.empty-hint {
  color: rgba(255, 255, 255, 0.6);
}

.sections-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-row {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(520px, 58%);
  gap: 16px;
  align-items: center;
  padding: 14px;
  border: 1px solid var(--ion-border-color);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
}

.section-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.section-info h2 {
  margin: 0;
  color: white;
  font-size: 1rem;
  font-weight: 700;
}

.section-key,
.section-role {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.82rem;
  word-break: break-word;
}

.section-status {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
}

.section-status--public {
  background: var(--ion-color-success);
  color: var(--ion-color-success-contrast);
}

.section-status--role {
  background: var(--ion-color-warning);
  color: var(--ion-color-warning-contrast);
}

.section-controls {
  display: grid;
  grid-template-columns: auto minmax(320px, 1fr) auto;
  gap: 10px;
  align-items: center;
}

.section-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  color: white;
  font-size: 0.8rem;
  white-space: nowrap;
}

.section-toggle--form {
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
}

.section-role-select {
  --background: rgba(255, 255, 255, 0.06);
  --background-activated: rgba(255, 255, 255, 0.08);
  --background-hover: rgba(255, 255, 255, 0.08);
  --border-radius: 10px;
  --color: white;
  --min-height: 52px;
  border-radius: 10px;
  min-width: 260px;
  width: 100%;
}

.section-role-select--disabled {
  opacity: 0.5;
}

.section-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  white-space: nowrap;
}

.header-title {
  letter-spacing: 0.08em;
}

/* Кнопки в стиле приложения: чёрно-оранжевый */
.admin-card ion-button:not([fill="clear"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: white;
  --color-activated: white;
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill="clear"]:not([color="danger"]) {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}

.admin-card ion-button[fill="clear"][color="danger"] {
  --color-activated: var(--ion-color-danger-tint);
}

@media (max-width: 1100px) {
  .section-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .section-controls {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .section-toggle {
    justify-content: space-between;
    width: 100%;
    padding: 2px 0;
  }

  .section-role-select {
    min-width: 0;
  }

  .section-actions {
    justify-content: space-between;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .admin-card {
    margin: 12px 10px;
  }

  .section-row {
    padding: 12px;
    border-radius: 12px;
  }

  .section-info h2 {
    font-size: 0.95rem;
  }

  .section-status {
    font-size: 0.68rem;
  }
}

:global(.admin-role-select-popover) {
  --width: min(460px, calc(100vw - 32px));
  --max-height: min(520px, calc(100vh - 96px));
}

:global(.admin-role-select-popover::part(content)) {
  width: min(460px, calc(100vw - 32px));
  max-height: min(520px, calc(100vh - 96px));
}

:global(.admin-role-select-popover ion-content) {
  --padding-top: 4px;
  --padding-bottom: 4px;
}

:global(.admin-role-select-popover ion-item) {
  --min-height: 50px;
}

:global(.admin-role-select-popover ion-label),
:global(.admin-role-select-popover ion-radio::part(label)) {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  line-height: 1.25;
}
</style>
